<template>
  <div class="forum-valores">
    <!-- Loading and Error States -->
    <div v-if="loading" class="loading-message typo-body-2">
      Cargando valores...
    </div>
    <div v-else-if="error" class="error-message typo-body-2">
      Error al cargar los valores: {{ error }}
    </div>

    <!-- Valores List -->
    <div v-else class="valores-container">
      <ul v-if="valores.length" class="valores-list">
        <li v-for="valor in valores" :key="valor.nid" class="valor-item">
          <div class="topic-wrapper">
            <div class="valor-main topic-link typo-body-2" @click="goToValor(valor)">
              <h3 class="valor-title">
                <span class="ticker">{{ valor.title }}</span>
              </h3>
              <div class="topic-meta">
                <span class="valor-price" :class="getPriceClass(valor)">
                  {{ valor.price }}
                </span>
                <span class="separator">•</span>
                <span class="last-update">Fecha de último envío: {{ formatDate(valor.last_comment_timestamp) }}</span>
                <span class="separator">•</span>
                <span class="comments-count">{{ valor.comment_count || 0 }} comentarios</span>
                <!-- Display new count if available and greater than 0 -->
                <span v-if="valor.new_count > 0" class="new-indicator">
                  <span class="separator">•</span>
                  <span class="new-count">{{ valor.new_count }} nuevo{{ valor.new_count > 1 ? 's' : '' }}</span>
                </span>
              </div>
            </div>
            
            <div class="valor-actions">
              <!-- Last Message Button -->
              <router-link
                :to="{ name: 'ForumThread', params: { nid: valor.nid }, query: { last: 'true' } }"
                class="last-message-btn typo-caption"
                target="_blank"
                title="Ir al último mensaje (abre en nueva pestaña)"
              >
                Último mensaje
              </router-link>
              <!-- <button class="action-btn view-chart-btn" @click.stop="viewChart(valor)">
                <i class="material-icons">show_chart</i>
                Ver gráfico
              </button> -->
            </div>
          </div>
        </li>
      </ul>
      <p v-else class="typo-body-2 no-valores-message">
        No hay valores para mostrar.
      </p>
    </div>

    <!-- Pagination -->
    <div class="forum-pagination" v-if="totalPages > 1">
      <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn typo-button">
        Anterior
      </button>
      <span class="page-info typo-body-2">
        Página {{ currentPage }} de {{ totalPages }}
      </span>
      <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn typo-button">
        Siguiente
      </button>
    </div>
  </div>
</template>

<script>
import forumApi from '@/services/forumApi';
import { mapGetters } from 'vuex';

const axios = forumApi;

export default {
  name: 'ForumValores',
  data() {
    return {
      valores: [],
      loading: true,
      error: null,
      currentPage: 1,
      itemsPerPage: 20,
      totalItems: 0
    };
  },
  computed: {
    ...mapGetters(['_g_IsUserLogged']),
    isLoggedIn() {
      return this._g_IsUserLogged;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    }
  },
  mounted() {
    this.fetchValores();
  },
  methods: {
    fetchValores() {
      this.loading = true;
      this.error = null;
      
      axios.get(`/forum/api/valores-drtortuga?items_per_page=${this.itemsPerPage}&page=${this.currentPage - 1}`)
        .then(response => {
          this.valores = response.data;
          
          // If we have a count header, use it for pagination
          const totalCount = response.headers['x-total-count'];
          if (totalCount) {
            this.totalItems = parseInt(totalCount);
          } else {
            // Estimate based on current page results
            this.totalItems = this.valores.length === this.itemsPerPage 
              ? this.itemsPerPage * (this.currentPage + 1) 
              : (this.currentPage - 1) * this.itemsPerPage + this.valores.length;
          }
          
          if (this.valores.length === 0) {
            this.error = 'No se encontraron valores';
          }
        })
        .catch(err => {
          console.error('Error fetching valores:', err);
          this.error = 'Error al cargar los valores';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    goToValor(valor) {
      this.$router.push({ name: 'ForumThread', params: { nid: valor.nid } });
    },
    viewChart(valor) {
      window.open(`/app/stockchart?s=${valor.ticker}`, '_blank');
    },
    getPriceClass(valor) {
      if (!valor.price) return '';
      
      // Check if price contains a + or - sign
      if (valor.price.includes('+')) {
        return 'positive-price';
      } else if (valor.price.includes('-')) {
        return 'negative-price';
      }
      return '';
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchValores();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchValores();
      }
    },
    goToLastMessage(valor) {
      // Navigate to the thread with the query parameter to go to the last message
      this.$router.push({ 
        name: 'ForumThread', 
        params: { nid: valor.nid },
        query: { last: 'true' }
      });
    },
    formatDate(dateValue) {
      // Handle null or undefined
      if (dateValue === null || dateValue === undefined) {
        return 'Fecha desconocida';
      }
      
      // If it's a number (Unix timestamp)
      if (typeof dateValue === 'number') {
        // Convert Unix timestamp (seconds) to JavaScript Date (milliseconds)
        const date = new Date(dateValue * 1000);
        return date.toLocaleString();
      } 
      // If it's a string that looks like a number
      else if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
        // Convert string timestamp to number
        const timestamp = parseInt(dateValue, 10);
        const date = new Date(timestamp * 1000);
        return date.toLocaleString();
      }
      // If it's already a date string
      else if (dateValue) {
        try {
          const date = new Date(dateValue);
          
          // Check if date is valid
          if (isNaN(date.getTime())) {
            return 'Fecha inválida';
          }
          
          return date.toLocaleString();
        } catch (e) {
          return 'Error de fecha';
        }
      }
      
      return 'Fecha desconocida';
    }
  }
};
</script>

<style scoped>
.forum-valores {
  width: 100%;
}

/* Reset de lista */
ul {
  padding: 0;
  margin: 0;
}

/* Valores container */
.valores-container {
  background-color: var(--color-surface);
  border-radius: 15px;
  padding: 20px;
  color: var(--color-text-primary);
  margin-bottom: 20px;
}

/* Valor items */
.valores-list {
  list-style-type: none;
}

.valor-item {
  margin-bottom: 12px;
  list-style-type: none;
  transition: transform 0.2s ease;
}

.topic-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--color-outline);
  margin-bottom: 12px;
  padding-bottom: 12px;
  transition: transform 0.2s ease;
  flex-wrap: wrap;
  gap: 8px;
}

.valor-item:last-child .topic-wrapper {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.valor-main {
  flex-grow: 1;
  margin-right: 10px;
  color: var(--color-text-primary);
  text-decoration: none;
  display: block;
  padding: 12px 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
  background-color: var(--color-surface-variant, rgba(0, 0, 0, 0.03));
  cursor: pointer;
}

.valor-main:hover {
  color: var(--color-highlight);
  background-color: var(--color-surface-hover, rgba(0, 0, 0, 0.05));
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.valor-title {
  margin: 0 0 5px 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  font-size: 1em;
}

.ticker {
  font-weight: bold;
  color: var(--color-text-primary);
  transition: all 0.3s ease;
  margin-right: 8px;
}

/* Remove hover effect from ticker itself since we'll apply it to the topic wrapper */
.topic-wrapper:hover .ticker {
  color: var(--color-highlight);
}

.topic-meta {
  display: flex;
  align-items: center;
  font-size: 0.85em;
  color: var(--color-text-secondary);
  margin-top: 4px;
  flex-wrap: wrap;
}

.separator {
  margin: 0 6px;
}

.comments-count {
  white-space: nowrap;
}

.last-update {
  white-space: nowrap;
}

.new-indicator {
  display: inline-flex;
  align-items: center;
}

.new-count {
  color: #f44336;
  font-weight: bold;
  white-space: nowrap;
}

.valor-price {
  font-weight: bold;
}

.positive-price {
  color: #4caf50;
}

.negative-price {
  color: #f44336;
}

.valor-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: var(--color-background);
  color: var(--color-text-primary);
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.action-btn:hover {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}

.view-chart-btn {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}

.view-chart-btn:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}

.no-valores-message {
  text-align: center;
  padding: 15px;
  color: var(--color-text-secondary);
}

/* Loading and Error Messages */
.loading-message,
.error-message {
  padding: 20px;
  background-color: var(--color-surface);
  border-radius: 15px;
  text-align: center;
  margin-bottom: 20px;
}

.error-message {
  border-left: 4px solid #f44336;
}

/* Pagination */
.forum-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background-color: var(--color-surface);
  border-radius: 15px;
}

.pagination-btn {
  background-color: var(--color-background);
  color: var(--color-text-primary);
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:hover:not(:disabled) {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}

/* Responsive Design */
@media (max-width: 768px) {
  .topic-wrapper {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .valor-actions {
    width: 100%;
    margin-top: 10px;
  }
}

.last-message-btn {
  background-color: var(--color-background);
  color: var(--color-text-primary);
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-size: 0.85em;
  white-space: nowrap;
  text-decoration: none;
}

.last-message-btn:hover {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}
</style> 